import React, {useMemo, useRef} from "react";
import {
    Card,
    Typography,
    Button,
    CircularProgress,
    Box,
    TextField,
    Chip,
} from "@mui/material";
import {alpha} from "@mui/material/styles";
import "./styles/CollaborateurPublicView.css";

const DIGITS = 6;

const CodePromptCard = ({code, setCode, onSubmit, loading, error}) => {
    const inputsRef = useRef([...Array(DIGITS)].map(() => React.createRef()));

    const digits = useMemo(() => {
        const clean = (code || "").replace(/\D/g, "").slice(0, DIGITS);
        return Array.from({length: DIGITS}, (_, i) => clean[i] || "");
    }, [code]);

    const focus = (i) => inputsRef.current[i]?.current?.focus();

    const handleChange = (i) => (e) => {
        const v = (e.target.value || "").replace(/\D/g, "");
        if (!v) {
            const base = digits.map((d, idx) => (idx === i ? "" : d)).join("");
            setCode(base);
            return;
        }
        const chunk = v.slice(0, DIGITS - i);
        const next = digits
            .map((d, idx) =>
                idx < i ? d : idx < i + chunk.length ? chunk[idx - i] : idx === i + chunk.length ? "" : digits[idx]
            )
            .join("");
        setCode(next);
        const nextIndex = Math.min(i + chunk.length, DIGITS - 1);
        requestAnimationFrame(() => focus(nextIndex));
    };

    const handleKeyDown = (i) => (e) => {
        if (e.key === "Backspace" && !digits[i] && i > 0) {
            e.preventDefault();
            const base = digits.map((d, idx) => (idx === i - 1 ? "" : d)).join("");
            setCode(base);
            requestAnimationFrame(() => focus(i - 1));
        }
        if (e.key === "ArrowLeft" && i > 0) focus(i - 1);
        if (e.key === "ArrowRight" && i < DIGITS - 1) focus(i + 1);
        if (e.key === "Enter" && code?.length === DIGITS && !loading) onSubmit();
    };

    const handlePaste = (e) => {
        const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, DIGITS);
        if (pasted) {
            e.preventDefault();
            setCode(pasted);
            requestAnimationFrame(() => focus(Math.min(pasted.length, DIGITS - 1)));
        }
    };

    const ready = (code || "").replace(/\D/g, "").length === DIGITS;

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                background:
                    "radial-gradient(1200px 320px at 10% -10%, rgba(0,0,0,0.04), transparent),linear-gradient(135deg, #e0f7fa, #f1f8e9)",
            }}
        >
            <Card
                className="glass-card"
                elevation={8}
                sx={{
                    p: 4,
                    borderRadius: 4,
                    maxWidth: 460,
                    width: "100%",
                    backdropFilter: "blur(12px)",
                    background: "rgba(255,255,255,.72)",
                    border: "1px solid rgba(255,255,255,.45)",
                    boxShadow: "0 18px 60px rgba(0,0,0,.15)",
                }}
            >
                <img
                    src="/assets/images/goandev-logo_old.png"
                    alt="Go&Dev"
                    style={{display: "block", margin: "0 auto 20px", maxHeight: 48}}
                />

                <Typography align="center" fontWeight={700} mb={1}>
                    Entrez le code de vérification
                </Typography>
                <Typography align="center" variant="body2" color="text.secondary" mb={2}>
                    Code à {DIGITS} chiffres envoyé par votre RH.
                </Typography>

                <Box
                    onPaste={handlePaste}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${DIGITS}, 1fr)`,
                        gap: 1,
                        mb: 2,
                    }}
                >
                    {digits.map((d, i) => (
                        <TextField
                            key={i}
                            inputRef={inputsRef.current[i]}
                            value={d}
                            onChange={handleChange(i)}
                            onKeyDown={handleKeyDown(i)}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    bgcolor: "#fff",
                                    boxShadow: `0 0 0 3px ${alpha("#1976d2", d ? 0.12 : 0)}`,
                                },
                            }} slotProps={{ htmlInput: {
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                                maxLength: 1,
                                style: {textAlign: "center", fontSize: 22, padding: "10px 0"},
                            } }}
                        />
                    ))}
                </Box>

                {error && (
                    <Chip
                        color="error"
                        variant="outlined"
                        label={error}
                        sx={{mb: 2, width: "100%"}}
                    />
                )}

                <Button
                    variant="contained"
                    fullWidth
                    disabled={!ready || loading}
                    onClick={onSubmit}
                    sx={{
                        py: 1.1,
                        borderRadius: 2,
                        fontWeight: 700,
                        background: "linear-gradient(90deg,#00c6ff,#0072ff)",
                        "&:hover": {background: "linear-gradient(90deg,#0072ff,#00c6ff)"},
                    }}
                >
                    {loading ? <CircularProgress size={22} sx={{color: "#fff"}}/> : "Valider"}
                </Button>
            </Card>
        </Box>
    );
};

export default CodePromptCard;
